import type { Family, RenderContext } from './context';
import type { Face } from './face-kit';
import { createAmbyss } from './full/ambyss';
import { createCampfire } from './full/campfire';
import { createCosmic } from './full/cosmic';
import { createDome } from './full/dome';
import { createDust } from './full/dust';
import { createExits } from './full/exits';
import { createGiants } from './full/giants';
import { createGiantsWake } from './full/giants-wake';
import { createClubs } from './full/clubs';
import { createFootball } from './full/football';
import { createButte } from './full/ground-butte';
import { createDesert } from './full/ground-desert';
import { JONGLEUR_DRAWING } from './full/jongleur';
import { createCanicule } from './full/canicule';
import { CANICULE_FACE } from './full/canicule-face';
import { createOrage } from './full/orage';
import { ORAGE_FACE } from './full/orage-face';
import { SPORTIF_DRAWING } from './full/sportif';
import { TATOUE_DRAWING } from './full/tatoue';
import { SUPERFAN_DRAWING } from './full/superfan';
import { ENRACINE_DRAWING } from './full/enracine';
import { createSpirits } from './full/spirits';
import { VEILLEUR_DRAWING } from './full/veilleur';
import { AVATAR_DRAWING } from './full/avatar';
import { createAvatarGiants } from './full/avatar-giants';
import { createAvatarMarks } from './full/avatar-marks';
import { createAvatarFamiliar } from './full/avatar-familiar';
import { createUnleashed } from './full/unleashed';
import { createFireflies } from './full/fireflies';
import { CELESTE_DRAWING } from './full/celeste';
import { createConstellations } from './full/constellations';
import { BABACOOL_DRAWING } from './full/babacool';
import { createDub } from './full/dub';
import { CIRCADIEN_DRAWING } from './full/circadien';
import { createSkies } from './full/skies';
import { POUSSIN_DRAWING } from './full/poussin';
import { createBand } from './full/band';
import { createGlowsticks } from './full/glowsticks';
import { createInk } from './full/ink';
import { createFauxContact } from './full/faux-contact';
import { FAUX_CONTACT_FACE } from './full/faux-contact-face';
import { createLarsen } from './full/larsen';
import { LARSEN_FACE } from './full/larsen-face';
import { createSand } from './full/ground-sand';
import { createMoustiques } from './full/moustiques';
import { MOUSTIQUES_FACE } from './full/moustiques-face';
import { createPath, createPathGround } from './full/path';
import { createPumpui } from './full/pumpui';
import type { ClassDrawing } from './drawings';
import type { PoseOf } from './enemies';
import type { StageLook } from './ground';
import type { Tree } from './ground-layout';
import { FESTIVAL, exitSpot } from '../data/full/festival';
import { SETS } from '../data/sets';

// The demo gets `full.demo.ts` in this file's place (ADR 0015).
export const FULL_LOOKS: readonly StageLook[] = [
  { decor: 'dome', ground: createSand, families: (ctx) => [createGiants(ctx), createDome(ctx)] },
  {
    decor: 'ambyss',
    ground: createButte,
    families: (ctx) => [createAmbyss(ctx), createCosmic(ctx)],
  },
  {
    decor: 'pumpui',
    ground: createDesert,
    families: (ctx) => [createPumpui(ctx), createDust(ctx)],
  },
  { decor: 'chemin', ground: createPathGround, families: (ctx) => [createPath(ctx)] },
];

export const FULL_FACES: Readonly<Record<string, Face>> = {
  orage: ORAGE_FACE,
  canicule: CANICULE_FACE,
  larsen: LARSEN_FACE,
  'faux-contact': FAUX_CONTACT_FACE,
  moustiques: MOUSTIQUES_FACE,
};
export const FULL_DRAWINGS: readonly ClassDrawing[] = [
  SPORTIF_DRAWING,
  TATOUE_DRAWING,
  SUPERFAN_DRAWING,
  ENRACINE_DRAWING,
  VEILLEUR_DRAWING,
  CELESTE_DRAWING,
  BABACOOL_DRAWING,
  POUSSIN_DRAWING,
  AVATAR_DRAWING,
  JONGLEUR_DRAWING,
  CIRCADIEN_DRAWING,
];
export const FULL_FAMILIES: readonly ((ctx: RenderContext, poseOf: PoseOf) => Family)[] = [
  createFootball,
  createInk,
  createGlowsticks,
  createSpirits,
  createFireflies,
  createConstellations,
  createDub,
  createSkies,
  createBand,
  createUnleashed,
  createAvatarMarks,
  createAvatarFamiliar,
  createAvatarGiants,
  createClubs,
  createOrage,
  createCanicule,
  createExits,
  createLarsen,
  createFauxContact,
  createCampfire,
  createGiantsWake,
  createMoustiques,
];

export const FULL_CLEARINGS: Readonly<Record<string, readonly Tree[]>> = Object.fromEntries(
  SETS.map((set) => [
    set.id,
    (FESTIVAL.exits[set.id] ?? []).map((exit) => exitSpot(set.arena, exit)),
  ]),
);
